import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { faFilter } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWatch } from "antd/es/form/Form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { FormItem, SelectField } from "@mapmycustomers/ui";

import stageIcon from "@app/assets/icons/deal-stage-dark.svg";
import { ConditionalFormFieldProps } from "@app/component/ConditionalFormField";
import EntityBadge from "@app/component/EntityBadge";
import DealLockReason from "@app/enum/DealLockReason";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import dealLockMessages from "@app/util/deal/dealLockMessages";
import {
  getSelectableFunnels,
  getSelectableNewDealDefaults,
  getSelectableStages,
} from "@app/util/deal/dealLockPickers";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

import styles from "./FunnelStageField.module.scss";

interface FunnelStageFieldProps extends ConditionalFormFieldProps {
  autoSelectFunnel?: boolean;
  currentFunnelId?: Funnel["id"]; // the edited deal's funnel, offered even when locked
  currentStageId?: Stage["id"]; // the edited deal's stage, offered even when read-only
  defaultFunnelId?: Funnel["id"];
  defaultStageId?: Stage["id"];
  disabled?: boolean;
  lockReason?: DealLockReason;
  required?: boolean;
}

const FunnelStageField = ({
  autoSelectFunnel = true,
  currentFunnelId,
  currentStageId,
  defaultFunnelId,
  defaultStageId,
  disabled,
  funnels,
  funnelStages,
  isCreateForm,
  lockReason,
  required,
}: FunnelStageFieldProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const form = useFormInstance();
  const analytics = useAnalytics();

  const { funnelLabel, stageLabel } = useMemo(
    () => ({
      funnelLabel: dealFieldModel.getByName(DealFieldName.FUNNEL)!.displayName,
      stageLabel: dealFieldModel.getByName(DealFieldName.STAGE)!.displayName,
    }),
    []
  );

  const funnelId: Funnel["id"] | undefined = useWatch("funnelId", form);
  const selectableFunnels = useMemo(
    () => getSelectableFunnels(funnels, funnelStages, currentFunnelId),
    [currentFunnelId, funnels, funnelStages]
  );
  const funnelOptions = useMemo(
    () => selectableFunnels.map(defaultSelectOptionMapper),
    [selectableFunnels]
  );
  const stageOptions = useMemo(
    () =>
      getSelectableStages(funnelId ? (funnelStages[funnelId] ?? []) : [], currentStageId).map(
        (stage) => ({
          key: stage.id,
          label: <EntityBadge entity={stage} />,
          value: stage.id,
        })
      ),
    [currentStageId, funnelStages, funnelId]
  );
  const lockTooltip = lockReason ? intl.formatMessage(dealLockMessages[lockReason]) : undefined;

  const stageField = dealFieldModel.getByName(DealFieldName.STAGE);
  const isStageFieldDisabled = !stageField?.isEditable && !isCreateForm;

  const handleFunnelChange = useCallback(
    (funnelId?: Funnel["id"]) => {
      form.setFieldsValue({ funnelId, stageId: undefined });
      // scope comes from the surface's AnalyticsContext, e.g. "Using: Add New Deal -> Select
      // Funnel" in the Create Deal modal; fires only on user changes (setFieldsValue bypasses it)
      analytics.using(["Select Funnel"], {
        funnel: funnels.find((funnel) => funnel.id === funnelId)?.name,
      });
    },
    [analytics, form, funnels]
  );

  const handleStageChange = useCallback(
    (stageId: Stage["id"]) => form.setFieldsValue({ stageId }),
    [form]
  );

  // Seeds the default funnel and stage into a form with no funnel. Reads the form, not the watched
  // funnelId, which can lag a render behind; funnelId stays a dep to re-seed after the rep clears it.
  useEffect(() => {
    if (!funnels.length || !autoSelectFunnel || form.getFieldValue("funnelId")) {
      return;
    }
    const defaults = getSelectableNewDealDefaults(
      { funnelId: defaultFunnelId, stageId: defaultStageId },
      funnels,
      funnelStages
    );
    if (defaults.funnelId) {
      form.setFieldsValue({ funnelId: defaults.funnelId, stageId: defaults.stageId });
    }
  }, [autoSelectFunnel, defaultFunnelId, defaultStageId, form, funnelId, funnels, funnelStages]);

  return (
    <div className={styles.container} data-id="js-funnel-staging-field">
      <FormItem
        label={funnelLabel}
        name="funnelId"
        required={required}
        rules={[{ required }]}
        showLabel={false}
      >
        <SelectField<Funnel["id"]>
          dropdownMatchSelectWidth={false}
          label={funnelLabel}
          locked={disabled || !!lockReason}
          lockTooltip={lockTooltip}
          onChange={handleFunnelChange}
          options={funnelOptions}
          placeholder={intl.formatMessage({
            id: "formFields.funnelStageField.funnel.placeholder",
            defaultMessage: "Select",
            description: "Funnel field placeholder",
          })}
          prefixIcon={<FontAwesomeIcon icon={faFilter} />}
          required={required}
        />
      </FormItem>

      <FormItem
        label={stageLabel}
        name={"stageId"}
        required={required}
        rules={[{ required }]}
        showLabel={false}
      >
        <SelectField<Stage["id"]>
          dropdownMatchSelectWidth={false}
          label={stageLabel}
          locked={isStageFieldDisabled || !!lockReason}
          lockTooltip={lockTooltip}
          onChange={handleStageChange}
          options={stageOptions}
          placeholder={intl.formatMessage({
            id: "formFields.funnelStageField.stage.placeholder",
            defaultMessage: "Select",
            description: "Stage field placeholder",
          })}
          prefixIcon={<img alt="stage icon" className={styles.stageIcon} src={stageIcon} />}
          required={required}
        />
      </FormItem>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
});

export default connect(mapStateToProps)(FunnelStageField);
